import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useState } from 'react';
import { ErroDeArea } from '../../design/Estados';
import { diaCurto, lerAnalisesDoGiro, lerRupturas } from '../../giro';
import type { AnalisesDoGiro, ItemDaAnalise, RupturasDoGiro as Dados } from '../../giro';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * RUPTURAS (§38 do 1.0) — o que VENDEU e o sistema diz que acabou. Não entra na missão (não se
 * manda procurar o que o sistema diz que não existe), e por isso aparece aqui: alguém procurou,
 * comprou, e a loja não tinha — ou tinha e o cadastro está errado. Lido da exclusão ESTOQUE_ZERO
 * de cada geração, sem tabela própria (uma verdade só).
 */

type Filtro = 'todos' | 'cronicos' | 'nunca' | 'sumidos';
const JANELAS = [7, 15, 30] as const;

const quando = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  });

export function RupturasDoGiro() {
  const [dias, setDias] = useState<number>(7);
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [d, setD] = useState<Dados | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTermo(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerRupturas(dias, termo).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [dias, termo]);
  useEffect(carregar, [carregar]);

  const cronico = (n: number) => n >= Math.min(3, dias);
  const linhas =
    d?.rupturas.filter((x) =>
      filtro === 'cronicos'
        ? cronico(x.diasEmRuptura)
        : filtro === 'nunca'
          ? !x.ultimaConferencia
          : filtro === 'sumidos'
            ? x.jaNaoLocalizado
            : true,
    ) ?? [];

  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-rupturas" />
      <div className="cv-corpo">
        <div className="cv-topo">
          <div className="cv-titulo cv-titulo-2l">
            <h1>Rupturas</h1>
            <span>
              Vendeu e o estoque confiável estava zerado · fica fora da missão e aparece aqui ·
              janela até {d ? diaCurto(d.dia) : '—'}
            </span>
          </div>
          <div className="gr-rup-controles">
            <div className="cv-chips" role="group" aria-label="Janela">
              {JANELAS.map((j) => (
                <button
                  key={j}
                  type="button"
                  className={`cv-chip ${dias === j ? 'cv-chip-sel' : ''}`}
                  onClick={() => setDias(j)}
                >
                  {j} dias
                </button>
              ))}
            </div>
            <input
              className="gr-rup-busca"
              value={busca}
              placeholder="Produto ou código"
              aria-label="Buscar produto"
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>
        {erro ? (
          <ErroDeArea oQue="as rupturas" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <>
            <div className="cv-chips">
              {(
                [
                  ['todos', 'Todas', d.resumo.produtos],
                  ['cronicos', 'Crônicas (3+ dias)', d.resumo.cronicos],
                  ['nunca', 'Nunca conferidas', d.resumo.nuncaConferidos],
                  ['sumidos', 'Já sumiram da prateleira', d.resumo.sumidosDaPrateleira],
                ] as const
              ).map(([k, rotulo, n]) => (
                <button
                  key={k}
                  type="button"
                  className={`cv-chip ${filtro === k ? 'cv-chip-sel' : ''}`}
                  onClick={() => setFiltro(k)}
                >
                  {rotulo} · <b>{n}</b>
                </button>
              ))}
              <Ajuda rotulo="os filtros de ruptura">
                <b>Crônicas</b>: zerado no HOS 3 dias ou mais. <b>Nunca conferidas</b>: ninguém
                contou esse produto na prateleira ainda. <b>Já sumiram da prateleira</b>: numa
                contagem alguém marcou &quot;não localizei&quot; — não é só estoque zerado.
              </Ajuda>
              <span style={{ flex: 1 }} />
              <span className="cv-cinza cv-peq">
                {String(Math.round(d.resumo.unidadesVendidas))} unidade(s) vendidas sem estoque
              </span>
            </div>
            <div className="cv-cartao cv-sem-pad">
              {linhas.length === 0 ? (
                <p className="cv-vazio">
                  {d.rupturas.length === 0
                    ? `Nenhuma ruptura nos últimos ${String(d.dias)} dias.`
                    : 'Nada neste filtro.'}
                </p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Setor</th>
                        <th className="cv-dir">Dias</th>
                        <th className="cv-dir">Vendido</th>
                        <th>Última conferência</th>
                        <th>Por quê</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((x, k) => (
                        <tr key={`${x.productId ?? x.codigo ?? x.nome}${String(k)}`}>
                          <td>
                            <div className="cv-forte">{x.nome}</div>
                            <div className="cv-mono cv-cinza cv-peq">{x.codigo ?? '—'}</div>
                          </td>
                          <td>{x.setor}</td>
                          <td
                            className={`cv-dir ${cronico(x.diasEmRuptura) ? 'gr-vermelho cv-forte' : ''}`}
                          >
                            {x.diasEmRuptura}
                            <div className="cv-cinza cv-peq">
                              {x.desde === x.ate
                                ? diaCurto(x.ate)
                                : `${diaCurto(x.desde)}–${diaCurto(x.ate)}`}
                            </div>
                          </td>
                          <td className="cv-dir">{String(Math.round(x.vendido))} un</td>
                          <td>
                            {x.ultimaConferencia ? (
                              <>
                                {quando(x.ultimaConferencia)}
                                <div className="cv-cinza cv-peq">
                                  {x.ultimaPrateleira === null
                                    ? ''
                                    : `${String(x.ultimaPrateleira)} na prateleira`}
                                </div>
                              </>
                            ) : (
                              <span className="gr-vermelho">nunca conferido</span>
                            )}
                            {x.jaNaoLocalizado && (
                              <div className="gr-vermelho cv-peq">já sumiu da prateleira</div>
                            )}
                          </td>
                          <td className="cv-cinza cv-peq">{x.porque}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
        <Analises />
      </div>
    </section>
  );
}

/** Análises dos últimos 30 dias (§26.18 do 1.0). Só leitura: nada aqui muda alvo. */
function Analises() {
  const [a, setA] = useState<AnalisesDoGiro | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerAnalisesDoGiro().then((r) => (r.ok ? setA(r.dados) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);
  if (erro) return <ErroDeArea oQue="as análises do Giro" aoTentar={carregar} />;
  if (!a) return <div className="cv-carregando" aria-busy="true" />;
  return (
    <div className="gr-analises">
      <div className="cv-titulo cv-titulo-2l">
        <h2 className="cv-h2">
          Análises · {diaCurto(a.desde)} a {diaCurto(a.ate)}
          <Ajuda rotulo="as análises">
            Listas dos últimos 30 dias para ajudar a decidir alvo e compra. Olhar não muda nada.
          </Ajuda>
        </h2>
        <span>{a.aviso}</span>
      </div>
      <div className="gr-analises-grade">
        <Lista
          titulo="Mais repostos"
          ajuda="Os produtos com mais unidades repostas no período: são os que mais saem da prateleira."
          itens={a.maisRepostos}
          fmt={(v) => `${String(v)} un`}
        />
        <Lista
          titulo="Abaixo do alvo (prateleira < 50%)"
          ajuda="Na média das contagens, a prateleira estava com menos da metade do alvo. Pode faltar estoque ou o alvo estar alto demais."
          itens={a.abaixoDoAlvo}
          fmt={(v) => `${String(v)}% do alvo`}
          alerta
        />
        <Lista
          titulo="Mais divergências"
          ajuda="Produtos em que mais vezes o auditor achou diferente do que foi informado na reposição."
          itens={a.maisDivergentes}
          fmt={(v) => `${String(v)} ${v === 1 ? 'caso' : 'casos'}`}
          alerta
        />
        <Lista
          titulo="Ruptura recorrente"
          ajuda="Produtos que mais dias ficaram fora do Giro por estoque zero no HOS: candidatos a comprar mais."
          itens={a.rupturaRecorrente}
          fmt={(v) => `${String(v)} ${v === 1 ? 'dia' : 'dias'}`}
          alerta
        />
      </div>
    </div>
  );
}

function Lista({
  titulo,
  ajuda,
  itens,
  fmt,
  alerta = false,
}: {
  titulo: string;
  ajuda: string;
  itens: readonly ItemDaAnalise[];
  fmt: (v: number) => string;
  alerta?: boolean;
}) {
  return (
    <div className="cv-cartao gr-analise">
      <h3>
        {titulo}
        <Ajuda rotulo={titulo.toLowerCase()}>{ajuda}</Ajuda>
      </h3>
      {itens.length === 0 ? (
        <p className="cv-cinza cv-peq">Nada nos últimos 30 dias.</p>
      ) : (
        <ol>
          {itens.map((x, k) => (
            <li key={`${x.productId ?? x.nome}${String(k)}`}>
              <span title={x.codigo ?? undefined}>{x.nome}</span>
              <b className={alerta ? 'gr-vermelho' : ''}>{fmt(x.valor)}</b>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
